import type { EventPayload } from '../types/destination'

export type MiddlewareFn = (
  event: EventPayload,
  next: () => void,
) => void | Promise<void>

/**
 * Middleware pipeline — processes events through a chain of middleware
 * before they reach destinations.
 */
export class MiddlewarePipeline {
  private middlewares: MiddlewareFn[] = []

  /** Register a middleware */
  use(fn: MiddlewareFn): void {
    this.middlewares.push(fn)
  }

  /**
   * Roda o evento pela cadeia. Cada middleware pode mutar o evento e precisa
   * chamar `next()` para continuar; não chamar DERRUBA o evento (nenhum destino
   * recebe). Devolve o evento (possivelmente mutado) ou `null` quando alguém
   * interrompeu a cadeia.
   *
   * É um laço com `await`, e não recursão com um `next` que se autoencadeia,
   * porque a versão anterior deduzia "a cadeia terminou?" comparando o índice
   * alcançado com o tamanho da lista. Essa dedução errava em dois casos:
   *
   *   - middleware ASSÍNCRONO (ou que chamasse `next()` dentro de callback,
   *     `setTimeout`, promise): quando o controle voltava, a função já havia
   *     retornado com o índice parado, então o evento era DERRUBADO e os
   *     middlewares seguintes nunca rodavam. Silenciosamente;
   *   - middleware ÚNICO que omitia `next()` para barrar o evento: o índice já
   *     estava no fim, a heurística concluía "terminou" e o evento PASSAVA —
   *     o oposto do que a documentação promete.
   *
   * Agora cada middleware é aguardado (o que também acomoda `async`) e a
   * decisão é explícita: chamou `next()` → continua; não chamou → derruba.
   */
  async execute<T extends EventPayload>(event: T): Promise<T | null> {
    for (const middleware of this.middlewares) {
      let advanced = false
      // `await` aceita tanto o retorno síncrono (`void`) quanto a promise de um
      // middleware `async` — é o que faz `next()` tardio ser respeitado.
      await middleware(event, () => { advanced = true })
      if (!advanced) return null
    }
    return event
  }

  /** Number of registered middlewares */
  get size(): number {
    return this.middlewares.length
  }
}
